/**
 * The Tenger brand values the widget falls back to for any appearance field an
 * application has not configured. Single source of truth for `accent.ts`,
 * `styles.ts`, `Button.tsx` and `fonts.ts`, which each previously carried their
 * own copy of the (off-brand) indigo.
 *
 * Lowercase hex to match the existing `styles.ts` / `accent.ts` convention.
 */

// `colors.ts` imports nothing, so this does not create a cycle with `accent.ts`
// (which imports both).
import { lighten } from './colors';

/** Catalina Blue. White on this is 14.75:1 — see the design doc, section 5. */
export const BRAND_ACCENT = '#041c74';
export const BRAND_ACCENT_TEXT = '#ffffff';

/**
 * Royal Blue, for links on the dialog's light surface: 6.6:1 on white. It was
 * Azure (#1d94ff), which is about 3:1 on white and fails AA as text; the
 * design language makes bright hues fills, not text. The modal had no link
 * until the confirmation gained one (I22642), which is when that showed. A
 * dark host's dialog switches links to Azure, where it reads (`styles.ts`).
 */
export const BRAND_LINK = '#3444e3';

/*
 * The neutrals and the error colour, from the brand palette in
 * `docs/design-language.md` design-language#colour-tokens (I19228).
 *
 * `styles.ts` was written against the TAILWIND SLATE RAMP: slate-900, slate-700,
 * slate-500, slate-300, slate-200, slate-100, slate-50 and red-600, as bare
 * hexes. CLAUDE.md prohibits exactly that ("no `slate-500`, no `red-600`"), and
 * the portal's guard has caught it there for a long time; the widget was the one
 * tree no guard walked, which is what I19228 is about.
 *
 * EIGHT RAMP VALUES COLLAPSE TO FIVE TOKENS, and that is the palette working
 * rather than information being lost. A ramp offers nine greys because it does
 * not know what they are for; the brand palette names four roles and stops. So
 * slate-900 and slate-700 were two body-text darks and are one ink; slate-300
 * and slate-200 were two border greys and are one hairline; slate-100 and
 * slate-50 were two recessed fills and are one mist. Anywhere that distinction
 * carried meaning, the meaning was accidental.
 *
 * These are lowercase to match the two tokens above, and the values are the
 * design language's own, copied rather than approximated.
 */

/** Gulf Blue. Body text. Design language design-language#colour-tokens calls this "deep body text". */
export const BRAND_INK = '#040c4c';

/** Muted. Secondary text, eyebrows, labels. */
export const BRAND_MUTED = '#565c7a';

/** Hair. Hairline rules and borders, and nothing else. */
export const BRAND_HAIR = '#d9deeb';

/** Mist. Recessed surfaces and hover fills. */
export const BRAND_MIST = '#f3f5fa';

/**
 * Danger Red. The one bright fill that also works as text: 5.01:1, so it is
 * legible as an error message as well as behind a white label (design-language#colour-tokens).
 */
export const BRAND_DANGER = '#d62828';

/**
 * The modal scrim, `BRAND_INK` at 55%.
 *
 * Spelled as `rgba()` rather than composed at runtime because it is one
 * constant, and written out here rather than inline in `styles.ts` so it cannot
 * drift from `BRAND_INK`. It was `rgba(15, 23, 42, 0.55)`, which is slate-900:
 * the same violation as the hexes above wearing different notation, and the
 * reason the guard checks for the ramp in both forms.
 */
export const BRAND_SCRIM = 'rgba(4, 12, 76, 0.55)';

/**
 * The redaction box drawn over a screenshot, `BRAND_INK` at 92%.
 *
 * Near-opaque on purpose: its job is to make what is underneath unreadable, so
 * the alpha is a functional requirement rather than a style choice, and it is
 * not a value to tune for taste.
 *
 * It was slate-900 at the same alpha while the box's BORDER was already
 * `#0f172a`, so the fill and its own outline came from two different palettes.
 * That is the kind of mismatch a guard that never walked this file could not
 * report.
 */
export const BRAND_REDACTION = 'rgba(4, 12, 76, 0.92)';

/** Sharp corners. The brand guide prohibits pills and rounded rectangles. */
export const DEFAULT_BORDER_RADIUS = 0;

/** Largest radius an admin may set — mirrors the API DTO's `.max(24)`. */
export const MAX_BORDER_RADIUS = 24;

/**
 * Clamps a configured radius into 0–24, defaulting anything that is not a
 * finite number. `WidgetConfig` crosses a network boundary, so TypeScript's
 * `number` is a claim about the wire format, not a runtime guarantee — and
 * both the modal's CSS text block and the launcher's inline style would
 * happily render `NaNpx`.
 */
export function clampBorderRadius(value: number | null | undefined): number {
  if (typeof value !== 'number' || !Number.isFinite(value)) return DEFAULT_BORDER_RADIUS;
  return Math.min(Math.max(Math.round(value), 0), MAX_BORDER_RADIUS);
}

/**
 * Body and UI type.
 *
 * `FT Poppins` FIRST, then the public `Poppins`. The private name is what
 * `fonts.ts` registers on the host document, namespaced so the widget cannot
 * repaint a customer's own page (see that file for the two regressions this
 * prevents). The public name is kept as the very next fallback deliberately:
 * our woff2 files are the *latin* subsets, so on a host page that genuinely
 * ships the real Poppins, that face serves the glyphs ours lack instead of
 * dropping straight to Helvetica Neue.
 *
 * Byte-identical copies live in the portal (`FONT_STACKS.brand` in
 * `AppearanceForm.tsx` and `BRAND_FONT_STACK` in `ButtonPreview.tsx`), which
 * cannot import from `@feedback/widget`. Change one, change all three — and
 * that is now a TEST rather than a request:
 * `apps/portal/src/admin/features/applications/brand-font-stack.test.ts` reads
 * the literal out of all three files and fails if they disagree.
 */
export const BRAND_FONT_STACK = "'FT Poppins', Poppins, 'Helvetica Neue', Arial, sans-serif";

/**
 * Titles only, and NOT admin-configurable — Montserrat Bold ALL CAPS for
 * headings is a brand rule, not a preference. Falls through to the body stack
 * so a custom `fontFamily` still governs everything below the heading. Same
 * private-then-public ordering as `BRAND_FONT_STACK`.
 */
export const BRAND_DISPLAY_STACK =
  "'FT Montserrat', Montserrat, 'FT Poppins', Poppins, 'Helvetica Neue', Arial, sans-serif";

/**
 * Mix-toward-white ratio for `--ft-accent-muted` (disabled Submit/confirm
 * buttons). Lives here rather than in `accent.ts` because `styles.ts` needs
 * the SAME ratio to derive its fallback — see `BRAND_ACCENT_MUTED`.
 */
export const MUTED_LIGHTEN_AMOUNT = 0.55;

/** Mix-toward-white ratio for `--ft-accent-subtle` (attachment chip background). */
export const SUBTLE_LIGHTEN_AMOUNT = 0.9;

/**
 * The brand-derived tints `styles.ts` uses as its `var()` FALLBACKS, for the
 * case where `accent.ts` emits no `--ft-accent-muted`/`--ft-accent-subtle` at
 * all — which is whenever `bgColor` fails `validHex` (an admin typing `navy`
 * into the free-text Background field, or an API caller sending `null`).
 *
 * Those fallbacks used to be indigo-300 and indigo-50 (the literals are not
 * repeated here — `prohibited-values.test.ts` scans this file, comments
 * included, and would fail on them). That meant the one path this branch could
 * not configure still rendered the exact hue the branch exists to remove.
 * Deriving them from `BRAND_ACCENT` with the ratios above is what makes the
 * configured and unconfigured paths agree:
 *
 *   lighten(BRAND_ACCENT, MUTED_LIGHTEN_AMOUNT)  = lighten('#041c74', 0.55)
 *   lighten(BRAND_ACCENT, SUBTLE_LIGHTEN_AMOUNT) = lighten('#041c74', 0.9)
 *
 * The resulting hexes are pinned in `styles-brand-fallbacks.test.ts`.
 *
 * Computed once at module load, not per render — `styles.ts` is a single
 * static template literal evaluated at import time and interpolates these as
 * constants.
 */
export const BRAND_ACCENT_MUTED = lighten(BRAND_ACCENT, MUTED_LIGHTEN_AMOUNT);
export const BRAND_ACCENT_SUBTLE = lighten(BRAND_ACCENT, SUBTLE_LIGHTEN_AMOUNT);
